/** Student answers to the interactive question elements on a canvas slide. */

import { z } from "zod";

export interface BaseQuestionAnswer {
  type: string;
}

export const ChoiceAnswerSchema = z.object({
  type: z.enum(["textChoice", "imageChoice"]),
  selectedIds: z.array(z.string()),
});

export const OddOutAnswerSchema = z.object({
  type: z.enum(["oddTextOut", "oddImageOut"]),
  selectedId: z.string().nullable(),
});

export const FillBlanksAnswerSchema = z.object({
  type: z.literal("fillBlanks"),
  answers: z.record(z.string(), z.string()), // blankId -> optionId
});

export const DragMatchingTextAnswerSchema = z.object({
  type: z.literal("dragMatchingText"),
  matches: z.record(z.string(), z.string()), // imageId -> textChoiceId (or placeholder ID)
});

export const DragMatchingImageAnswerSchema = z.object({
  type: z.literal("dragMatchingImage"),
  matches: z.record(z.string(), z.string()), // textId -> imageChoiceId
});

export const OrderAnswerSchema = z.object({
  type: z.enum(["orderImage", "orderText"]),
  // Item IDs in the student's chosen order; "" at a slot whose typed or placed value matches no
  // real item (orderText only).
  order: z.array(z.string()),
  // orderText only: slotIndex -> the raw text typed there, kept even when it matched no item so
  // the review UI can show it instead of a blank.
  typedTexts: z.record(z.string(), z.string()).optional(),
});

/** Only student-editable letters are stored; a revealed first letter is excluded from validation
 * and scoring, so it never appears here. */
export const SpellingAnswerSchema = z.object({
  type: z.literal("spelling"),
  letters: z.record(z.string(), z.string()), // letterIndex -> typed character
});

export const SpeakingAnswerSchema = z.object({
  type: z.literal("speaking"),
  answers: z.record(z.string(), z.string()), // sentenceIndex -> optionIndex
});

export const QuestionAnswerSchema = z.discriminatedUnion("type", [
  ChoiceAnswerSchema,
  OddOutAnswerSchema,
  FillBlanksAnswerSchema,
  DragMatchingTextAnswerSchema,
  DragMatchingImageAnswerSchema,
  OrderAnswerSchema,
  SpellingAnswerSchema,
  SpeakingAnswerSchema,
]);

/** Element ID -> that element's answer. */
export const QuestionAnswersMapSchema = z.record(z.string(), QuestionAnswerSchema);

export type ChoiceAnswer = z.infer<typeof ChoiceAnswerSchema>;
export type OddOutAnswer = z.infer<typeof OddOutAnswerSchema>;
export type FillBlanksAnswer = z.infer<typeof FillBlanksAnswerSchema>;
export type DragMatchingTextAnswer = z.infer<typeof DragMatchingTextAnswerSchema>;
export type DragMatchingImageAnswer = z.infer<typeof DragMatchingImageAnswerSchema>;
export type OrderAnswer = z.infer<typeof OrderAnswerSchema>;
export type SpellingAnswer = z.infer<typeof SpellingAnswerSchema>;
export type SpeakingAnswer = z.infer<typeof SpeakingAnswerSchema>;
export type QuestionAnswer = z.infer<typeof QuestionAnswerSchema>;
export type QuestionAnswersMap = z.infer<typeof QuestionAnswersMapSchema>;
